import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import { formatActivityDetails } from "./activityDetails";
import ActivityDetailModal from "./ActivityDetailModal";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import SectionHeader from "../../Elements/SectionHeader";
import './ActivityPage.css';

const LIMIT = 50;

// Acción canónica (backend) → etiqueta legible + tono de color.
const ACTION_META = {
  "stock.entry":     { label: "Entrada de stock",  tone: "emerald" },
  "stock.exit":      { label: "Salida de stock",   tone: "rose" },
  "stock.adjust":    { label: "Ajuste de stock",   tone: "amber" },
  "stock.set_total": { label: "Total de stock",    tone: "amber" },
  "stock.reconcile_total": { label: "Total cuadrado", tone: "amber" },
  "stock.transfer":  { label: "Traspaso de stock", tone: "cyan" },
  "sale.create":     { label: "Venta",             tone: "lime" },
  "sale.reverse":    { label: "Venta anulada",     tone: "rose" },
  "sale.reverse_units": { label: "Anulación parcial", tone: "rose" },
  "saldo.aportacion":{ label: "Aportación",        tone: "violet" },
  "saldo.aportacion_anulada": { label: "Aportación anulada", tone: "rose" },
  "socio.fee_reversed": { label: "Cuota anulada",  tone: "rose" },
  "socio.create":    { label: "Alta de socio",     tone: "emerald" },
  "socio.update":    { label: "Edición de socio",  tone: "slate" },
  "staff.create":    { label: "Alta de personal",  tone: "cyan" },
  "owner.create":    { label: "Alta de propietario", tone: "violet" },
  // Catálogo con efecto inmediato en el POS. Sin estas entradas la fila se pinta con
  // la acción cruda («product.delete») en gris, que es legible pero feo y no dice tono.
  // Asistencia (plan CRUO §J). Sin estas dos entradas el badge salía crudo
  // («socio.entrada» en gris) y —lo que más molestaba— el desplegable Acción no
  // ofrecía filtrar entradas ni salidas, porque se construye de este mismo mapa.
  // `slate` para la salida y no `rose`: aquí el rojo ya significa anulación o salida
  // de stock, y una salida de socio no es ninguna de las dos.
  "socio.entrada":   { label: "Entrada de socio",  tone: "emerald" },
  "socio.salida":    { label: "Salida de socio",   tone: "slate" },
  // Caja. Este mapa alimenta también el desplegable «Acción», así que sin estas cuatro
  // entradas no se podría filtrar por ellas ni aunque el backend las escriba.
  // Las correcciones en ámbar: no son un error, pero son lo que hay que mirar al
  // cuadrar un turno — mismo tono que los ajustes de stock.
  "caja.apertura":        { label: "Caja abierta",       tone: "emerald" },
  "caja.apertura_editada":{ label: "Apertura corregida", tone: "amber" },
  "caja.cierre":          { label: "Caja cerrada",       tone: "slate" },
  "caja.cierre_editado":  { label: "Cierre corregido",   tone: "amber" },
  "product.delete":     { label: "Producto borrado",     tone: "rose" },
  "product.retire":     { label: "Producto retirado",    tone: "amber" },
  "product.reactivate": { label: "Producto reactivado",  tone: "emerald" },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800",
  rose:    "bg-rose-100 text-rose-800",
  amber:   "bg-amber-100 text-amber-800",
  cyan:    "bg-cyan-100 text-cyan-800",
  lime:    "bg-lime-100 text-lime-800",
  slate:   "bg-slate-100 text-slate-700",
  violet:  "bg-violet-100 text-violet-800",
};

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action, tone: "slate" };
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASSES[meta.tone] || TONE_CLASSES.slate}`}>
      {meta.label}
    </span>
  );
}

// Resumen de una línea, para que la columna diga algo sin obligar a abrir nada.
// El detalle de verdad (las líneas de la venta, los movimientos del producto) vive en
// el modal: aquí solo caben los pares que el summary no repite ya.
function DetailPairs({ pairs }) {
  if (!pairs.length) return null;
  return (
    <div className="mt-0.5 text-xs text-slate-500">
      {pairs.map((p, i) => (
        <span key={p.label} className="whitespace-nowrap">
          {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
          <span className="text-slate-400">{p.label}:</span> {p.value}
        </span>
      ))}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ActivityPage() {
  const [token, , , venueID, venueName, , , accessPrivileges] = useContext(UserContext);

  // Espejo del filtro por rol del backend (activity_services.visible_actions_for):
  // el staff solo ve la operativa diaria, así que su desplegable no debe ofrecer
  // "Alta de personal/propietario" (el servidor las filtraría igualmente).
  const visibleActionMeta = useMemo(() => {
    if (accessPrivileges === "staff") {
      const teamActions = new Set(["staff.create", "owner.create"]);
      return Object.fromEntries(
        Object.entries(ACTION_META).filter(([key]) => !teamActions.has(key))
      );
    }
    return ACTION_META;
  }, [accessPrivileges]);

  const [items, setItems] = useState([]);
  // id → nombre de ubicación. `null` = no disponible (ver el efecto de carga).
  const [locations, setLocations] = useState(null);
  // Fila abierta en el modal de detalle (drill-down).
  const [detailItem, setDetailItem] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filtros
  const [actionFilter, setActionFilter] = useState("");
  const [since, setSince] = useState("");
  // Ventana cerrada: antes solo se podía decir "desde", nunca acotar un turno.
  const [until, setUntil] = useState("");
  const [q, setQ] = useState("");
  // Lo tecleado (`q`) y lo que se ha pedido al servidor (`qAplicado`) son cosas
  // distintas: sin esperar a que pare de escribir, «crua» serían cuatro consultas.
  const [qAplicado, setQAplicado] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQAplicado(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async (nextOffset) => {
    if (!token || !venueID) { setLoading(false); return; }
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(nextOffset) });
    if (actionFilter) params.set("action", actionFilter);
    // La búsqueda la hace el SERVIDOR. Antes se filtraba aquí, sobre las filas ya
    // cargadas: con «Cargar más» de 50 en 50, buscar «CRUA» en un periodo entero era
    // imposible — encontrabas lo de la página actual y parecía que no había más
    // (reporte de Kong, 19-ago, cuadrando stock).
    if (qAplicado.trim()) params.set("q", qAplicado.trim());
    // Los campos aceptan fecha Y hora (turnos que no empiezan a medianoche). Si
    // solo llega la fecha, se asume el día completo: 00:00 → 23:59:59.
    if (since) {
      const d = new Date(since);
      if (!isNaN(d)) params.set("since", d.toISOString());
    }
    if (until) {
      const d = new Date(until);
      if (!isNaN(d)) {
        if (!until.includes("T")) d.setHours(23, 59, 59, 999);
        else d.setSeconds(59, 999);
        params.set("until", d.toISOString());
      }
    }
    try {
      const res = await apiFetch(`/v1/venues/${venueID}/activity?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const newItems = data.items || [];
      setItems((prev) => (nextOffset === 0 ? newItems : [...prev, ...newItems]));
      setHasMore(Boolean(data.has_more));
      setOffset(nextOffset + newItems.length);
    } catch (e) {
      setError(e.message || "Error cargando la actividad");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token, venueID, actionFilter, since, until, qAplicado]);

  // Recarga desde cero cuando cambian los filtros de servidor (acción/fecha/texto).
  useEffect(() => {
    setItems([]);
    setOffset(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, venueID, actionFilter, since, until, qAplicado]);

  // El log guarda las ubicaciones como UUID, así que hace falta el mapa id → nombre.
  // `/v1/stock/locations` exige permiso de edición de productos, pero la Actividad la
  // ve cualquier STAFF: un 403 aquí es esperable y NO debe romper la página — se queda
  // sin nombres de ubicación y el resto del detalle se muestra igual.
  useEffect(() => {
    if (!token || !venueID) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/v1/stock/locations?venue_id=${venueID}`, { headers: authHeaders });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const byId = {};
        for (const loc of data || []) byId[loc.id] = loc.name;
        setLocations(byId);
      } catch {
        /* sin ubicaciones: se omiten, no se pinta el UUID crudo */
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueID, authHeaders]);

  // El detalle legible se calcula una vez por fila: lo pinta la tabla y también lo
  // busca el filtro de texto (el placeholder ya prometía "detalle").
  const rows = useMemo(
    () => items.map((it) => ({ it, pairs: formatActivityDetails(it.action, it.details, locations, it.summary) })),
    [items, locations]
  );

  // Sin filtro en cliente: filtrar OTRA VEZ aquí escondería filas que el servidor sí
  // considera resultado. El servidor busca en el texto guardado (resumen, actor,
  // acción y detalle); estas filas ya vienen filtradas.
  const visible = rows;

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      {/* Era un hero con degradado —radial de lima sobre slate-900 → verde oscuro
          → lima pálido, radio 28, sombra y texto blanco—: la única sección del
          producto que no se parecía a ninguna otra, y encima con la marca
          revertida. El rótulo «Auditoría» iba en versalitas sobre el degradado y
          se pierde: el título ya dice qué es esto. */}
      <SectionHeader
        title="Actividad"
        subtitle={`Quién hizo qué, sobre qué y cuándo — stock, ventas, aportaciones, socios y personal.${venueName ? ` · ${venueName}` : ""}`}
        sticky={false}
      />

      {/* Filtros */}
      <div className="wa-actividad-filters flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm">
        <label className="flex flex-col text-xs font-medium text-slate-600">
          Acción
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          >
            <option value="">Todas</option>
            {Object.entries(visibleActionMeta).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </label>
        <label className="wa-actividad-label-right flex flex-col text-xs font-medium text-slate-600">
          Desde (fecha y hora)
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          />
        </label>
        <label className="flex flex-col text-xs font-medium text-slate-600">
          Hasta (fecha y hora)
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          />
        </label>
        <label className="wa-actividad-label-right flex flex-1 flex-col text-xs font-medium text-slate-600">
          Buscar
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Producto, usuario, detalle…"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          />
        </label>
        {(actionFilter || since || until || q) && (
          <button
            onClick={() => { setActionFilter(""); setSince(""); setUntil(""); setQ(""); }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Limpiar
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      {/* Tabla */}
      <div className="overflow-x-auto rounded-2xl bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <TableHead>
            <Th className="whitespace-nowrap">Fecha</Th>
            <Th className="whitespace-nowrap">Usuario</Th>
            <Th className="whitespace-nowrap">Acción</Th>
            <Th>Detalle</Th>
          </TableHead>
          <tbody>
            {visible.map(({ it, pairs }) => (
              <Tr
                key={it.id}
                onClick={() => setDetailItem(it)}
                // Sin esto el drill-down solo existe para quien use ratón.
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailItem(it);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Ver detalle: ${it.summary || it.action}`}
                // El hover lo pone ya la fila del sistema; aquí solo el foco.
                className="cursor-pointer focus:bg-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--primary)]"
                title="Ver detalle"
              >
                {/* `align-top` en las celdas, no en la fila: la columna «Detalle»
                    lleva varias líneas y centrarla descoloca a las demás. */}
                <Td className="whitespace-nowrap align-top text-[var(--muted-foreground)]">{formatWhen(it.created_at)}</Td>
                <Td className="align-top">
                  <div className="font-medium text-[var(--foreground)]">{it.actor_email || "—"}</div>
                  {it.actor_role && <div className="text-xs text-[var(--muted-foreground)]">{it.actor_role}</div>}
                </Td>
                <Td className="align-top"><ActionBadge action={it.action} /></Td>
                <Td className="align-top">
                  <div>{it.summary || "—"}</div>
                  <DetailPairs pairs={pairs} />
                </Td>
              </Tr>
            ))}
            {!loading && visible.length === 0 && (
              <tr>
                <Td align="center" colSpan={4} className="px-4 py-10 text-[var(--muted-foreground)]">
                  {q ? "Nada coincide con la búsqueda." : "Todavía no hay actividad registrada."}
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailItem && (
        <ActivityDetailModal
          item={detailItem}
          token={token}
          venueID={venueID}
          locations={locations}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* Paginación */}
      <div className="flex items-center justify-center gap-3">
        {loading && <span className="text-sm text-slate-400">Cargando…</span>}
        {!loading && hasMore && (
          <button
            onClick={() => load(offset)}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Cargar más
          </button>
        )}
      </div>
    </div>
  );
}
